import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { RequiredInput } from "@/components/common/required-input";
import { toast } from "sonner";
import type { ZodSchema } from "zod";

interface AddEntityDialogProps<T> {
  entityName: string;
  schema: ZodSchema<T>;
  defaultValues: () => T;
  onCreate: (data: T) => Promise<any>;
  buttonLabel?: string;
  fieldLabel?: string;
  successMessage?: (name: string) => string;
  conflictMessage?: (name: string) => string;
}

export function AddEntityDialog<T extends { name: string }>({
  entityName,
  schema,
  defaultValues,
  onCreate,
  buttonLabel,
  fieldLabel = "Name",
  successMessage,
  conflictMessage,
}: AddEntityDialogProps<T>) {
  const [open, setOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: defaultValues(),
    onSubmit: async ({ value }) => {
      try {
        const validated = schema.parse(value);
        
        const trimmedData = {
          ...validated,
          name: validated.name.trim(),
        };
        
        await onCreate(trimmedData);

        const message = successMessage
          ? successMessage(trimmedData.name)
          : `${entityName} "${trimmedData.name}" created successfully`;
        toast.success(message);

        form.reset();
        setOpen(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;

        if (statusCode === 409) {
          const message = conflictMessage
            ? conflictMessage((value as any).name)
            : `A ${entityName.toLowerCase()} with the name "${(value as any).name}" already exists`;
          setServerError(message);
        } else {
          toast.error(`Failed to create ${entityName.toLowerCase()} "${(value as any).name}"`);
          setOpen(false);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          form.reset();
          setServerError(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus />
          {buttonLabel || `Add ${entityName}`}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New {entityName}</DialogTitle>
        </DialogHeader>
        <Separator />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      {fieldLabel}
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value as string}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value as any);
                        setServerError(null);
                      }}
                      placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />
          </FieldGroup>
          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit">Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
